import {
  EContinueFinancingDisableReason,
  EDecision,
  EPaymentFrequency,
  EPaymentPlan,
  EWorksheetStatus,
} from '@if/core/types'
import { formatCurrency, formatDate, formatDateTime } from '@if/core/utils'
import { Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Divider, IconButton, Link, Paper, Tooltip, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import { useAppSelector } from '@src/data/store'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import ifXpressWorksheetEffects from '@src/data/store/IfXpressWorksheet/ifxpress-worksheet-effects'
import type { EditIfXpressWorksheetDto, IfXpressWorksheet } from '@src/data/types/IfXpressWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { computeTotalInsurance } from '@src/services/WorksheetComputer'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CreditApplication, ETermMax, User } from '../../../data/types'
import getIFinanceMaxPaymentDateGivenActivationDate from '../viewCreditApplicationPage-selectors'

interface Props {
  creditApplication: CreditApplication
  worksheet: IfXpressWorksheet
  user: User | null
  editDisabled: boolean
  continueFinancingDisableReasonList: EContinueFinancingDisableReason[]
  handleRefinanceCreditApplication: () => void
  onWorksheetHistoryClicked: () => void
}

const IfXpressWorksheetInformationSection = ({
  creditApplication,
  worksheet,
  user,
  editDisabled,
  continueFinancingDisableReasonList,
  handleRefinanceCreditApplication,
  onWorksheetHistoryClicked,
}: Props) => {
  const dispatchEffect = useSideEffect()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const viewWorksheetDisabled = worksheet === null
  const editWorksheetDisabled =
    creditApplication.finalCreditDecision.decision !== EDecision.Approved ||
    !user?.rights.canEditWorksheet ||
    editDisabled
  const merchantPaymentPlan = useAppSelector(creditSelectors.getMerchantPaymentPlan)
  // event handlers
  const createWorksheet = () => {
    const route = `/Applications/ifxpress/${creditApplication.id}/worksheet/edit`

    if (worksheet === null) {
      const paymentAmount =
        (creditApplication.finalCreditDecision.maxAmountFinanced ?? 0) >= (creditApplication.requestedLoanAmount ?? 0)
          ? creditApplication.requestedLoanAmount
          : creditApplication.finalCreditDecision.maxAmountFinanced

      const newWorksheet: EditIfXpressWorksheetDto = {
        amountRequested: paymentAmount,
        deliveryOn: formatDate(new Date()),
        firstPaymentOn: getIFinanceMaxPaymentDateGivenActivationDate(new Date()),
        paymentFrequency: EPaymentFrequency.Monthly,
        term: merchantPaymentPlan ? merchantPaymentPlan.loanTerm : ETermMax.twelve,
        includeInsurance: false,
        versionTag: undefined,
        fundConfirmationNote: null,
        paymentPlanId: merchantPaymentPlan ? merchantPaymentPlan.id : EPaymentPlan.RegularDailyInterests,
        creditApplicationId: creditApplication.id,
        status: EWorksheetStatus.Draft,
      }
      dispatchEffect(ifXpressWorksheetEffects.create(newWorksheet))
        .then(() => navigate(route))
        .catch(reportErrorToConsole)
    } else {
      navigate(route)
    }
  }

  const tooltipTextContinueFinancing = () => {
    if (continueFinancingDisableReasonList.length > 0) {
      return (
        <div>
          {continueFinancingDisableReasonList.map((r) => (
            <Typography key={r}>{t(`continueFinancing.${r}`)}</Typography>
          ))}
        </div>
      )
    }
    return null
  }
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ButtonGroup variant="outlined" size="large">
              <Button
                id="btnEditWorksheet"
                component={Link}
                href={`/Applications/ifxpress/${creditApplication.id}/worksheet`}
                variant="outlined"
                disabled={viewWorksheetDisabled}
              >
                {t('worksheet.financing')}
              </Button>
              <Button onClick={createWorksheet} disabled={editWorksheetDisabled} size="small">
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
            <Tooltip title={tooltipTextContinueFinancing()}>
              <span>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleRefinanceCreditApplication}
                  disabled={continueFinancingDisableReasonList.length !== 0}
                  style={{
                    padding: '10px',
                    fontSize: '13px',
                  }}
                >
                  {t('common.continueFinancing')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            alignContent="center"
            divider={<Divider orientation="vertical" flexItem sx={{ height: 25, alignSelf: 'center' }} />}
          >
            <Typography variant="body1" component="div" gutterBottom>
              {t('common.LastModification')} : {worksheet && formatDateTime(worksheet?.updatedOn)}
            </Typography>
            <IconButton
              onClick={onWorksheetHistoryClicked}
              disabled={worksheet === null}
              sx={[
                {
                  fontSize: 'large',
                },
                worksheet === null
                  ? {
                      color: '',
                    }
                  : {
                      color: 'primary.main',
                    },
              ]}
            >
              <HistoryIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.decision')} :{' '}
              {worksheet?.finalDecision.decision &&
                t(`EWorksheetDecision.${worksheet.finalDecision.decision}`, { ns: 'enum' })}
            </Typography>
            {worksheet !== null && (
              <Typography variant="body1" component="div" gutterBottom>
                Promo :{' '}
                {worksheet?.paymentPlanId !== EPaymentPlan.RegularDailyInterests ? t('common.yes') : t('common.no')}
              </Typography>
            )}
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet?.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.price')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.protectionPlan')} : {formatCurrency(computeTotalInsurance(worksheet))}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} : {formatCurrency(worksheet?.totalAmountFinanced)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default IfXpressWorksheetInformationSection
